import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { reviewCardHref } from "./reviewCardLink";   // A2-581 lead 6

// A2-576 (operator 2026-10-27) — sales held for their VAT rate are in no box
// until answered; say how much, so the boxes below do not look final.
// A2-603: a sale date in the screen's own language; the raw text if unreadable.
function formatDate(iso, lang) {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return new Intl.DateTimeFormat(lang === "cn" ? "zh-CN" : (lang || undefined), { dateStyle: "medium" }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d);
  }
}

export default function HeldForAnswerBanner({ held }) {
  const { t, i18n } = useTranslation();
  const { calcId } = useParams();
  if (!held || !held.rows) return null;
  // A2-637 Owner D (card map D-1..D-3): the held rows by what releases them,
  // each part linking to the card that answers it (api/_held_rows.waiting_parts);
  // an older payload without parts keeps the one link it had
  const parts = Array.isArray(held.parts) ? held.parts.filter((p) => p.card) : null;
  const rerun = Array.isArray(held.parts) ? held.parts.find((p) => p.reason === "rerun") : null;
  const earlier = Array.isArray(held.earlier_sales) ? held.earlier_sales : [];
  const earlierSales = earlier.filter((s) => s.earlier);
  const uploadSales = earlier.filter((s) => !s.earlier && s.upload);
  const elsewhereSales = earlier.filter((s) => !s.earlier && !s.upload);
  const title = (flag) => t(`flag_titles.${flag}`, { defaultValue: t("ui.country_return.held.card_default", { defaultValue: "its card" }) });
  const saleLabel = (s) => {
    const date = s.sale_date ? formatDate(s.sale_date, i18n && i18n.language) : null;
    const ref = s.invoice
      ? t("ui.country_return.held.sale_invoice", { invoice: s.invoice, defaultValue: `Invoice ${s.invoice}` })
      : s.order
        ? t("ui.country_return.held.sale_order", { order: s.order, defaultValue: `Order ${s.order}` })
        : t("ui.country_return.held.sale_unnamed", { defaultValue: "A sale with no invoice number" });
    const sold = date ? t("ui.country_return.held.sale_sold", { ref, date, defaultValue: `${ref}, sold ${date}` }) : ref;
    return s.upload ? t("ui.country_return.held.sale_in_upload", { sale: sold, upload: s.upload,
      defaultValue: `${sold}, in “${s.upload}”` }) : sold;
  };
  let amount = held.net;
  try {
    amount = new Intl.NumberFormat(undefined, { style: "currency", currency: held.currency }).format(Number(held.net));
  } catch { /* an unknown currency code: show the plain figure */ }
  return (
    <div
      data-testid="held-for-answer-banner"
      className="text-sm bg-amber-50 dark:bg-amber-950/40 border border-l-4 border-amber-500 rounded-lg p-3 mb-4 text-amber-950 dark:text-amber-100"
    >
      <div className="font-semibold">
        {t("ui.country_return.held.title", { amount, rows: held.rows,
          defaultValue: `${amount} of sales (${held.rows} rows) wait for your answer and are in no box yet` })}
      </div>
      {!parts && (
        <div className="text-xs opacity-90 mt-0.5 leading-snug">
          {t("ui.country_return.held.why", {
            defaultValue: "Their VAT is 0 and nothing in the file says why. Answer each one — zero-rated, exempt, or the rate — then re-run the calculation. Until then the boxes below leave them out." })}
        </div>
      )}
      {parts && parts.map((p) => (
        <div className="text-xs mt-1" data-testid={`held-for-answer-part-${p.card}`} key={`${p.reason}-${p.card}`}>
          {p.reason === "own"
            ? t("ui.country_return.held.part_own", { count: p.rows,
                defaultValue: `${p.rows} wait for their own answer:` })
            : t("ui.country_return.held.part_sale", { count: p.rows,
                defaultValue: `${p.rows} ${p.rows === 1 ? "is a credit note" : "are credit notes"} that follow${p.rows === 1 ? "s" : ""} ${p.rows === 1 ? "its" : "their"} sale - answer the sale:` })}
          {" "}
          <Link to={reviewCardHref(p.card, calcId)} data-testid={`held-for-answer-link-${p.card}`} className="font-semibold underline">
            {title(p.card)} →
          </Link>
        </div>
      ))}
      {held.unconverted_rows > 0 && (
        <div className="text-xs mt-1" data-testid="held-for-answer-unconverted">
          {t("ui.country_return.held.unconverted", { n: held.unconverted_rows,
            defaultValue: `${held.unconverted_rows} of them have no exchange rate yet and are not in the amount above.` })}
        </div>
      )}
      {rerun && (
        <div className="text-xs mt-1" data-testid="held-for-answer-rerun">
          {t("ui.country_return.held.rerun", { count: rerun.rows,
            defaultValue: `${rerun.rows} credit notes follow sales already answered: re-run the calculation to release them.` })}
        </div>
      )}
      {[["earlier", earlierSales], ["upload", uploadSales], ["elsewhere", elsewhereSales]].map(([kind, group]) => {
        if (!group.length) return null;
        const n = group.reduce((m, s) => m + (Number(s.rows) || 1), 0);
        const lead = kind === "upload"
          ? t("ui.country_return.held.upload", { count: group.length, n,
              defaultValue: `${n} of them ${n === 1 ? "is a credit note" : "are credit notes"} for ${group.length === 1 ? "a sale" : "sales"} in another upload of this client, still waiting for an answer there. Answer ${group.length === 1 ? "that sale" : "those sales"} in that upload's calculation, then re-run this one:` })
          : kind === "earlier"
          ? t("ui.country_return.held.earlier", { count: n,
              defaultValue: `${n} of them ${n === 1 ? "is a credit note" : "are credit notes"} for ${group.length === 1 ? "a sale" : "sales"} in an earlier period that still ${group.length === 1 ? "waits" : "wait"} for an answer. Answer ${group.length === 1 ? "that sale" : "those sales"} in ${group.length === 1 ? "its" : "their"} own period's calculation, then re-run this one:` })
          : t("ui.country_return.held.elsewhere", { count: n,
              defaultValue: `${n} of them ${n === 1 ? "is a credit note" : "are credit notes"} for ${group.length === 1 ? "a sale" : "sales"} that ${group.length === 1 ? "is" : "are"} not on this return and still ${group.length === 1 ? "waits" : "wait"} for an answer — excluded, or on another country's return. Answer ${group.length === 1 ? "it" : "them"} there, then re-run this one:` });
        return (
          <div className="text-xs mt-1" data-testid={`held-for-answer-${kind}`} key={kind}>
            {lead}
            <ul className="list-disc ml-5 mt-0.5">
              {group.slice(0, 5).map((s, i) => <li key={`${s.invoice}-${s.order}-${s.sale_date}-${i}`}>{saleLabel(s)}</li>)}
            </ul>
            {group.length > 5 && (
              <div>{t("ui.country_return.held.earlier_more", { n: group.length - 5, defaultValue: `and ${group.length - 5} more` })}</div>
            )}
          </div>
        );
      })}
      {!parts && (
        <Link to={reviewCardHref("MISSING_VAT_RATE_ON_DOMESTIC", calcId)} data-testid="held-for-answer-link"
              className="inline-block mt-2 text-xs font-semibold underline">
          {t("ui.country_return.held.answer", { defaultValue: "Answer them on Step 4 →" })}
        </Link>
      )}
    </div>
  );
}
